import PropTypes from 'prop-types';
import React, { useContext } from 'react';
import { groupActions } from '../../Includes/GroupActionTypes';
import GroupCTA from '../Elements/GroupCTA/GroupCTA';
import GroupTypeDisplay from '../Elements/GroupTypeDisplay/GroupTypeDisplay';
import GroupBanner from '../GroupBanner/GroupBanner';
import GroupLogo from '../GroupLogo/GroupLogo';
import MemberCountDisplay from '../MemberCountDisplay/MemberCountDisplay';
import { ReactRouterLink } from '../ReactRouterLink';
import './Header.scss';
import { GroupContext } from '../../../contexts/ContextCreator';

const Header = (props) => {
    const { hideGroupsCTA } = props;

    // Defaults in case a group Provider was not used (create group)
    const {
        group = {},
        userRelation = { role: null },
        hasPermission = () => false,
        isMember = () => false,
        acceptInvitation = () => {},
    } = useContext(GroupContext);

    const {
        name,
        description,
        logo,
        hash,
        headerImage,
        contentIsPrivate,
        memberCount,
        previewMembers,
    } = group;

    const isAdmin = hasPermission(groupActions.EDIT_GROUP);

    const renderBanner = () => {
        // default banner shown to admin when banner has not been set
        const defaultBannerSrc = '/v1565121217/Groups/default-admin-banner.png';

        const hasBannerImage = headerImage && headerImage.src;

        if (hasBannerImage) {
            return <GroupBanner src={headerImage.src} alt={headerImage.alt} />;
        }

        if (!hasBannerImage && isAdmin) {
            return <GroupBanner src={defaultBannerSrc} alt="default banner" />;
        }

        return <div className="blank-banner" />;
    };

    const renderMemberInfo = () => {
        const isGroupMember = isMember();

        if (isGroupMember || (!isGroupMember && !contentIsPrivate)) {
            return (
                <MemberCountDisplay
                    count={memberCount}
                    previewMembers={previewMembers}
                />
            );
        }
        return <div className="blank-member-box" />;
    };

    const wrapComponentLink = (Component, url) => {
        if (props.isCreate) return Component;
        if (props.useGenericLink) return <a href={url}>{Component}</a>;

        return <ReactRouterLink to="/">{Component}</ReactRouterLink>;
    };

    return (
        <div className="component-Groups-Header">
            {wrapComponentLink(renderBanner(), `/groups/${hash}`)}
            <div className="content">
                <div className="group-logo-container">
                    {wrapComponentLink(
                        <GroupLogo logo={logo} isAdmin={isAdmin} name={name} />,
                        `/groups/${hash}`
                    )}
                </div>
                <div className="group-info">
                    <h1 className="title">{name}</h1>
                    <p className="description">{description}</p>
                </div>
                {group && group.hash && (
                    <div className="group-options">
                        <GroupTypeDisplay
                            group={group}
                            userRelation={userRelation}
                            hasPermission={hasPermission}
                        />
                        {!hideGroupsCTA ? (
                            <GroupCTA
                                group={group}
                                userRelation={userRelation}
                                isMember={isMember}
                                acceptInvitation={acceptInvitation}
                            />
                        ) : null}
                        {renderMemberInfo()}
                    </div>
                )}
            </div>
        </div>
    );
};

Header.propTypes = {
    /**
     * whether to use router link
     */
    useGenericLink: PropTypes.bool,
    /**
     * whether the header is in create group mode
     */
    isCreate: PropTypes.bool,
    /**
     * whether to hide or show groups cta
     */
    hideGroupsCTA: PropTypes.bool,
};

Header.defaultProps = {
    useGenericLink: false,
    isCreate: false,
    hideGroupsCTA: false,
};

export default React.memo(Header);
